import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Ticket, ShieldCheck } from "lucide-react";
import { EVENT_TYPES } from "@/components/TournamentCreateForm";

const input = "w-full bg-pb-elevated border border-pb-border px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-pb-red";
const label = "block text-[9px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1";
const BRACKET_LABELS = ["1st", "2nd", "3rd", "4th", "5–6", "7–8", "9–16", "17–32"];

export default function PbrAdminConsole() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [wildcards, setWildcards] = useState([]);
  const [wcSearch, setWcSearch] = useState("");
  const [wcResults, setWcResults] = useState([]);
  const [wcPlayer, setWcPlayer] = useState(null);
  const [wcDisc, setWcDisc] = useState(EVENT_TYPES[0]);
  const [fieldDisc, setFieldDisc] = useState(EVENT_TYPES[0]);
  const [field, setField] = useState(null);

  const load = useCallback(() => {
    api.get("/pbr/config").then(({ data }) => setCfg(data));
    api.get("/pbr/wildcards").then(({ data }) => setWildcards(data));
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    setField(null);
    api.get("/pbr/qualification", { params: { discipline: fieldDisc } }).then(({ data }) => setField(data)).catch(() => setField(false));
  }, [fieldDisc, wildcards]);
  useEffect(() => {
    if (wcSearch.trim().length < 2) { setWcResults([]); return; }
    const id = setTimeout(() => api.get("/members/search", { params: { q: wcSearch } }).then(({ data }) => setWcResults(data)), 300);
    return () => clearTimeout(id);
  }, [wcSearch]);

  const [rebuilding, setRebuilding] = useState(false);
  const [rebuilt, setRebuilt] = useState(null);

  if (!cfg) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-pb-red" /></div>;

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/pbr/config", patch);
      setCfg(data);
      toast.success("PBR settings saved — rankings recalculate automatically");
    } catch (e) { toast.error("Couldn't save settings"); }
    setSaving(false);
  };
  const setNum = (k, v) => setCfg({ ...cfg, [k]: Number(v) || 0 });
  const setTable = (lvl, idx, v) => {
    const levels = { ...cfg.levels };
    const t = [...levels[lvl].table]; t[idx] = Number(v) || 0;
    levels[lvl] = { ...levels[lvl], table: t, max: idx === 0 ? Number(v) || 0 : levels[lvl].max };
    setCfg({ ...cfg, levels });
  };
  const rebuildCounts = async () => {
    setRebuilding(true);
    try {
      const { data } = await api.post("/admin/pbr/rebuild-tournament-stats");
      setRebuilt(data);
      toast.success(`Rebuilt from ${data.tournaments} completed tournaments — ${data.changed} player record${data.changed === 1 ? "" : "s"} corrected`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setRebuilding(false);
  };
  const grantWc = async () => {
    if (!wcPlayer) return;
    try {
      await api.post("/pbr/wildcards", { player_id: wcPlayer.id, discipline: wcDisc });
      toast.success(`Wild card granted to ${wcPlayer.name}`);
      setWcPlayer(null); setWcSearch("");
      api.get("/pbr/wildcards").then(({ data }) => setWildcards(data));
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't grant wild card"); }
  };
  const revokeWc = async (id) => {
    await api.delete(`/pbr/wildcards/${id}`);
    toast.success("Wild card revoked");
    api.get("/pbr/wildcards").then(({ data }) => setWildcards(data));
  };

  return (
    <div className="space-y-6" data-testid="pbr-admin-console">
      <div className="border border-amber-500/40 bg-amber-500/5 p-4 flex flex-wrap items-center gap-3" data-testid="pbr-rebuild-panel">
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">Tournaments-played counts</div>
          <div className="text-xs text-zinc-400 mt-1">One tournament counts <b className="text-white">once</b> toward District / Regional / State / National gates, no matter how many divisions a player enters. Rebuild recomputes every player's played / matches / wins from completed results.</div>
          {rebuilt && <div className="text-[10px] text-emerald-300 mt-1" data-testid="pbr-rebuild-result">Last run: {rebuilt.tournaments} tournaments · {rebuilt.players} players · {rebuilt.changed} corrected</div>}
        </div>
        <button data-testid="pbr-rebuild-btn" onClick={rebuildCounts} disabled={rebuilding}
          className="bg-amber-400 text-black font-black uppercase tracking-widest text-[10px] px-4 py-2.5 hover:bg-amber-300 active:scale-95 transition-all disabled:opacity-50 flex items-center gap-2">
          {rebuilding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Rebuild tournament counts
        </button>
      </div>
      {/* Core settings */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[["qual_size", "Nationals Field (Top N)"], ["best_n", "Best Results Counted"], ["bubble_size", "Bubble Size"],
          ["participation_bonus", "Participation Bonus"], ["win_bonus", "Win Bonus / Match"], ["max_wildcards", "Wild Cards / Discipline"]].map(([k, l]) => (
          <div key={k}>
            <label className={label}>{l}</label>
            <input data-testid={`pbr-cfg-${k}`} type="number" value={cfg[k]} onChange={(e) => setNum(k, e.target.value)} className={input} />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-4 text-xs">
        {[["bonuses_enabled", "Match & participation bonuses"], ["regional_auto_qual", "Regional champs auto-qualify"],
          ["defending_champ_auto_qual", "Defending national champ auto-berth"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-zinc-300 cursor-pointer">
            <input data-testid={`pbr-cfg-${k}`} type="checkbox" checked={!!cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.checked })} className="accent-[#BEF264]" />
            {l}
          </label>
        ))}
        <button data-testid="pbr-cfg-save" disabled={saving} onClick={() => save(cfg)}
          className="ml-auto flex items-center gap-1.5 bg-pb-red text-black text-[10px] font-black uppercase tracking-widest px-4 py-2 hover:bg-[#BEF264] transition-colors disabled:opacity-50">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {/* Point tables */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Point Tables by Level (editable — 1st place sets the max)</div>
        <div className="overflow-x-auto border border-pb-border">
          <table className="w-full text-xs">
            <thead><tr className="text-[9px] uppercase tracking-widest text-zinc-500">
              <th className="text-left px-3 py-2">Level</th>
              {BRACKET_LABELS.map((b) => <th key={b} className="px-2 py-2 text-right">{b}</th>)}
            </tr></thead>
            <tbody>
              {Object.entries(cfg.levels).map(([lvl, v]) => (
                <tr key={lvl} className="border-t border-pb-border/60">
                  <td className="px-3 py-1.5 font-bold uppercase text-zinc-300">{v.label}</td>
                  {v.table.map((pts, i) => (
                    <td key={i} className="px-1 py-1.5">
                      <input data-testid={`pbr-table-${lvl}-${i}`} type="number" value={pts}
                        onChange={(e) => setTable(lvl, i, e.target.value)}
                        className="w-16 bg-pb-elevated border border-pb-border px-1.5 py-1 text-right text-xs text-white focus:border-pb-red outline-none" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Wild cards */}
      <div className="border border-pb-border p-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold mb-3 flex items-center gap-1.5"><Ticket className="w-3.5 h-3.5" /> National Wild Cards — {new Date().getFullYear()} Season</div>
        <div className="flex flex-wrap gap-2 items-end mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <label className={label}>Player</label>
            <input data-testid="pbr-wc-search" value={wcPlayer ? wcPlayer.name : wcSearch}
              onChange={(e) => { setWcPlayer(null); setWcSearch(e.target.value); }} placeholder="Search players…" className={input} />
            {wcResults.length > 0 && !wcPlayer && (
              <div className="absolute z-20 left-0 right-0 top-full bg-black border border-pb-border max-h-40 overflow-y-auto">
                {wcResults.map((p) => (
                  <button key={p.id} data-testid={`pbr-wc-pick-${p.id}`} onClick={() => { setWcPlayer(p); setWcResults([]); }}
                    className="w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-white/5">{p.name}</button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className={label}>Discipline</label>
            <select data-testid="pbr-wc-discipline" value={wcDisc} onChange={(e) => setWcDisc(e.target.value)} className={input}>
              {EVENT_TYPES.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <button data-testid="pbr-wc-grant" disabled={!wcPlayer} onClick={grantWc}
            className="text-[10px] font-black uppercase tracking-widest border border-amber-500/60 text-amber-400 px-4 py-2 hover:bg-amber-400 hover:text-black transition-colors disabled:opacity-40">
            Grant Wild Card
          </button>
        </div>
        {wildcards.length === 0 ? <div className="text-xs text-zinc-600">No wild cards issued this season.</div> : (
          <div className="space-y-1" data-testid="pbr-wc-list">
            {wildcards.map((w) => (
              <div key={w.id} className="flex items-center gap-3 text-xs border border-pb-border/60 px-3 py-1.5">
                <span className="text-white font-semibold">{w.player_name}</span>
                <span className="text-zinc-500">{w.discipline}</span>
                <button data-testid={`pbr-wc-revoke-${w.id}`} onClick={() => revokeWc(w.id)}
                  className="ml-auto text-zinc-600 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Nationals field preview */}
      <div className="border border-pb-border p-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-green-400 font-bold flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Nationals Field Preview</div>
          <select data-testid="pbr-field-discipline" value={fieldDisc} onChange={(e) => setFieldDisc(e.target.value)} className={`${input} w-auto`}>
            {EVENT_TYPES.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          {field && <span className="text-[10px] text-zinc-500">{field.auto_count} auto · {field.ranking_count} by ranking · {field.wildcard_count} wild cards</span>}
        </div>
        {field === null && <Loader2 className="w-5 h-5 animate-spin text-pb-red" />}
        {field && field.field?.length === 0 && <div className="text-xs text-zinc-600" data-testid="pbr-field-empty">No qualified players yet — the field fills as sanctioned events complete.</div>}
        {field && field.field?.length > 0 && (
          <div className="space-y-1" data-testid="pbr-field-list">
            {field.field.map((f, i) => (
              <div key={f.player_id} className="flex items-center gap-3 text-xs border border-pb-border/60 px-3 py-1.5">
                <span className="font-display font-black text-zinc-500 w-6">{i + 1}</span>
                <span className="text-white font-semibold">{f.name}</span>
                <span className={`text-[8px] font-black uppercase tracking-wider border px-1.5 py-0.5 ${f.type === "ranking" ? "border-pb-cyan/40 text-pb-cyan" : f.type === "wildcard" ? "border-amber-500/50 text-amber-400" : "border-green-500/50 text-green-400"}`}>{f.pathway}</span>
                <span className="ml-auto text-zinc-500">{f.rank ? `#${f.rank} · ` : ""}{f.points} pts</span>
              </div>
            ))}
            <div className="text-[9px] text-zinc-600 pt-1">{field.note}</div>
          </div>
        )}
      </div>
    </div>
  );
}
